'use client';

import React, { ReactElement, useCallback, useContext, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { FirebaseError } from '@firebase/util';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../contexts/auth';
import { ProxyLoginContext } from '../../../contexts/ProxyLoginContext';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { usePageLayout } from '../../../hooks/usePageLayout';
import { useCompanyId } from '../../../hooks/useCompanyId';
import useSnackbar from '../../../hooks/useSnackbar';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Grid2,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  buttonTextIconLarge,
  gray100,
  gray20,
  gray40,
  gray60,
  gray80,
  primaryButtonBackgroundColor,
  secondaryButtonBackgroundColor,
  tertiaryButtonBackgroundColor,
  white,
  white_overlay80,
} from '../../../constants/colors';
import { boldXxl, spHeaderTitle, regularSm, regularXs } from '../../../constants/location';
import MetaTitle from '../../utils/MetaTitle';
import SideBar from '../../uiParts/menu/SideBar';
import {
  CloudUploadOutlined,
  DeleteOutline,
  FileDownloadOutlined,
  MoreVert,
  PersonOutline,
  SwapHoriz,
} from '@mui/icons-material';
import {
  DocumentData,
  DocumentSnapshot,
  Timestamp,
  doc,
  onSnapshot,
  updateDoc,
} from 'firebase/firestore';
import InfiniteScroll from 'react-infinite-scroll-component';
import CustomLoading from '../../uiParts/form/CustomLoading';
import DescriptionIcon from '@mui/icons-material/Description';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import DeleteModal from '../../uiParts/modal/DeleteModal';
import FileExportDialog from '../../uiParts/modal/FileExportDialog';
import OcrLimitDialog from '../../uiParts/modal/OcrLimitDialog';
import PatternSelectModal from '../../uiParts/modal/PatternSelectModal';
import UploadModal from '../../uiParts/modal/UploadModal';
import AssigneeChangeDialog from '../../uiParts/modal/AssigneeChangeDialog';
import { assigneeLabels } from '../../../constants/assignee';
import { resolveAssigneeDisplay, type AssigneeOption } from '../../../utils/assigneeDisplay';
import { resolveDefaultAssignee } from '../../../api/firebase/firestore/company/resolveDefaultAssignee';
import { getUser, getUsersByCompanyId } from '../../../api/firebase/firestore/user/read';
import {
  ocrStatus,
  processingStatus,
  processingStatusLabel,
  registerStatus,
  resultStatus,
  fileProcessingType,
  geminiModel,
  geminiThinkingBudget,
  exportFileType,
  exportEncoding,
  FREE_OCR_PATTERN_ID,
  PATTERN_ID_OTHER,
  OCR_UPLOAD_MAX_TOTAL_PAGES,
  OCR_UPLOAD_MAX_PAGES_PER_FILE,
  GEMINI_MODEL_DEFAULT,
} from '../../../constants/enum';
import { isPdfFile, getPdfPageCount, calculateTotalPageCount } from '../../../utils/pdfToImage';
import { useTotalPageCount } from '../../../hooks/useTotalPageCount';
import { useUserMonthlyOcrLimit } from '../../../hooks/useUserMonthlyOcrLimit';
import { inputUploadSchema } from '../../../validation/input';
import { ResultType, ResultWithUserType, WatchSearchItemsType } from '../../../types/result';
import type { UserType } from '../../../types/user';
import AiOcrSearchForm from '../../uiParts/parts/AiOcrSearchForm';
import {
  getPatternResultsOneTimePaginated,
  searchPatternResultsByAlgolia,
} from '../../../api/firebase/firestore/result/read';
import {
  getCompanyForAccount,
  getMonthlyUsageCountByUserId,
} from '../../../api/firebase/firestore/company/read';
import { getPattern } from '../../../api/firebase/firestore/pattern/read';
import { db } from '../../../api/firebase/config';
import { deleteResult } from '../../../api/firebase/firestore/result/delete';
import { editResult } from '../../../api/firebase/firestore/result/update';
import {
  getCreditRateByPlan,
  calculateOcrLimitInfo,
  getUserMonthlyOcrLimit,
  getUserMonthlyOcrLimitMessage,
  getOcrLimitOverMessage,
} from '../../../utils/userUsageLimit';
import { executeProvisionalOcrUsage } from '../../../utils/provisionalOcrUsage';
import { handleReOcrPatternResult } from '../../../api/firebase/function/handleReOcrPatternResult';
import { getOcrCreditRateWithModel } from '../../../constants/plans';
import { isOperatorStaff } from '../../../utils/permission';
import { getUserErrorMessage } from '../../../modules/error';
import { useDetailMode } from '../../../hooks/useDetailMode';
import { getDisplayName } from '../../../utils/displayName';
import { useUploadDispatch } from '../../../contexts/UploadContext';
import HiStoryIcon from '../../assets/hi-story-icon.png';
import NoAccountIcon from '../../assets/Account circle.svg';
import {
  getPatternResultsForExport,
  getPatternResultsCountByStatus,
} from '../../../api/firebase/firestore/result/read';
import {
  generateFreeAiOcrVerticalRows,
  generateFreeAiOcrFileName,
} from '../../../utils/freeAiOcr/fileExportUtils';
import { createFileExportHistory } from '../../../api/firebase/firestore/fileExportHistory/create';
import { exportFile } from '../../../api/firebase/function/exportFileFunction';
import { PROXY_DISPLAY_NAME } from '../../../constants/labels';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import DeletedBadge from '../../uiParts/parts/DeletedBadge';
import { OcrFileNameStatus, OcrStatusIcon } from '../../uiParts/parts/OcrStatusFileInfo';
import Encoding from 'encoding-japanese';
import Papa from 'papaparse';

// アップロード制限は constants/enum.ts に集約（メール取り込み経路とも共通の値）
const MAX_TOTAL_PAGES = OCR_UPLOAD_MAX_TOTAL_PAGES;
const MAX_PAGES_PER_FILE = OCR_UPLOAD_MAX_PAGES_PER_FILE;
const RESULT_LIMIT = 100;

const FreeAiOcr = (): ReactElement => {
  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const { user, company, isLoading } = useContext(AuthContext);
  const { operatorId, operatorRole } = useContext(ProxyLoginContext);
  const { isDetailView } = useDetailMode();
  const { companyId: effectiveCompanyId, isProxyMode } = useCompanyId();
  const { startUpload } = useUploadDispatch();
  // アップロードモーダルはファイル数に応じて maxHeight: calc(100vh - 32px) まで伸びるため、
  // 代理モードではバナー分を差し引かないと下端がはみ出す（#3310）
  const proxyDialogSx = useProxyDialogSx({ fullHeight: true });
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const [baseRemainingCount, setBaseRemainingCount] = useState<number | null>(null);
  const [baseRemainingJpy, setBaseRemainingJpy] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  // データ取得関連
  const [realtimeData, setRealtimeData] = useState<ResultWithUserType[]>([]);
  const [searchData, setSearchData] = useState<ResultWithUserType[]>([]);
  const [lastVisible, setLastVisible] = useState<DocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [showDataFlg, setShowDataFlg] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSearchRefreshing, setIsSearchRefreshing] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(processingStatus.processing);
  const [processingCount, setProcessingCount] = useState<number | null>(null);
  const [completedCount, setCompletedCount] = useState<number | null>(null);
  const [outputtedCount, setOutputtedCount] = useState<number | null>(null);
  const [watchSearchItems, setWatchSearchItems] = useState<WatchSearchItemsType>({
    fileName: null,
    responsiblePerson: null,
    assignee: null,
    startDate: null,
    endDate: null,
    freeWord: null,
    ocrStatus: null,
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // 担当者（#2642）
  const [assigneeOptions, setAssigneeOptions] = useState<AssigneeOption[]>([]);
  const [selectedAssigneeUid, setSelectedAssigneeUid] = useState<string>('');
  // 担当者変更ダイアログ（単一/一括）
  const [assigneeDialogOpen, setAssigneeDialogOpen] = useState<boolean>(false);
  const [assigneeDialogMode, setAssigneeDialogMode] = useState<'single' | 'bulk'>('single');
  const [assigneeDialogTargets, setAssigneeDialogTargets] = useState<string[]>([]);
  const [assigneeDialogCurrent, setAssigneeDialogCurrent] = useState<string | undefined>(undefined);
  const [assigneeDialogLabel, setAssigneeDialogLabel] = useState<string | undefined>(undefined);
  const [uploadModalOpen, setUploadModalOpen] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState<boolean>(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState<boolean>(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string>('');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTargetId, setMenuTargetId] = useState<string>('');
  const [fileExportModalOpen, setFileExportModalOpen] = useState<boolean>(false);
  const [fileExportTargetId, setFileExportTargetId] = useState<string | null>(null);
  const [fileCountOverText] = useState<string>('');
  const [reclassifyModalOpen, setReclassifyModalOpen] = useState<boolean>(false);
  const [reclassifyLoading, setReclassifyLoading] = useState<boolean>(false);
  const [singleReclassifyTargetId, setSingleReclassifyTargetId] = useState<string>('');
  const [limitDialogOpen, setLimitDialogOpen] = useState<boolean>(false);

  // ファイル処理タイプの状態管理
  const [processingTypeValue, setProcessingTypeValue] = useState<number>(
    fileProcessingType.pagePerResult,
  );
  // pagePerResultモード用: ファイルごとのページ数キャッシュ（ファイル名 → ページ数）
  const [pdfPageCountCache, setPdfPageCountCache] = useState<Map<string, number>>(new Map());
  // Geminiモデル選択の状態管理
  const [selectedGeminiModel, setSelectedGeminiModel] = useState<number>(GEMINI_MODEL_DEFAULT);
  // FreeAiOcr では Pro モデル許可フラグは常に false
  const isProAllowedPattern = false;
  // 思考設定の状態管理（Gemini 2.5: thinkingBudget数値, Gemini 3: thinkingLevel文字列）
  const [thinkingBudgetValue, setThinkingBudgetValue] = useState<number | null>(
    geminiThinkingBudget.disabled,
  );
  const [thinkingLevelValue, setThinkingLevelValue] = useState<string | null>(null);

  const unsubscribeRef = useRef<(() => void) | null>(null);
  const fetchIdRef = useRef<number>(0);
  const fetchResultsRef = useRef<
    ((lastVis: DocumentSnapshot | null, cond?: WatchSearchItemsType) => Promise<void>) | null
  >(null);

  // React Hook Form
  const {
    control,
    handleSubmit,
    reset,
    watch,
    trigger,
    setError,
    formState: { errors },
  } = useForm<{ images: File[] }>({
    resolver: yupResolver(inputUploadSchema),
    mode: 'onChange',
    defaultValues: { images: [] },
  });

  // 合計ページ数を計算（PDFはページ数分、画像は1ページとしてカウント）
  const watchedImages = watch('images') ?? [];
  const { totalPageCount, isCalculating } = useTotalPageCount(watchedImages);

  // 個人別 月間OCR上限をリアルタイム監視（#3133）
  // 会社枠と同じくファイル選択時点でインラインのバリデーションエラーを出すために事前購読する。
  const { getLimitMessage: getUserLimitMessage } = useUserMonthlyOcrLimit(
    user?.uid,
    effectiveCompanyId ?? user?.company_id,
    isProxyMode,
  );

  const formatTimestamp = (ts: Timestamp): string => {
    if (!ts) return '-';
    const date = ts.toDate();
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${y}/${m}/${d} ${hh}:${mm}`;
  };

  const hasActiveSearch =
    !!watchSearchItems.freeWord?.trim() ||
    !!watchSearchItems.fileName?.trim() ||
    !!watchSearchItems.responsiblePerson ||
    !!watchSearchItems.assignee ||
    !!watchSearchItems.startDate ||
    !!watchSearchItems.endDate ||
    watchSearchItems.ocrStatus != null;

  const showData = hasActiveSearch ? (isSearchRefreshing ? [] : searchData) : realtimeData;
  const isAllSelected = showData.length > 0 && selectedIds.size === showData.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < showData.length;

  const checkCreditBeforeReclassify = (newPatternId: string, isBulk: boolean): boolean => {
    if (isProxyMode) return true;
    if (newPatternId === PATTERN_ID_OTHER) return true;
    if (!company) return true;
    const count = isBulk ? selectedIds.size : 1;
    const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
    if (limitInfo.totalRemainingCount < count) {
      setReclassifyModalOpen(false);
      setLimitDialogOpen(true);
      return false;
    }
    return true;
  };

  const handleBulkReclassify = (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): void => {
    if (!checkCreditBeforeReclassify(newPatternId, true)) return;
    void executeBulkReclassify(newPatternId, itemList);
  };

  const executeBulkReclassify = async (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): Promise<void> => {
    setReclassifyModalOpen(false);
    setReclassifyLoading(true);
    const targetIds = Array.from(selectedIds);
    const totalCount = targetIds.length;
    let provisionalDeducted = false;
    const targetPageCounts = new Map<string, number>();
    for (const id of targetIds) {
      const result = showData.find(d => d.id === id);
      targetPageCounts.set(id, result?.originalFiles?.length || result?.files?.length || 1);
    }
    const totalPages = Array.from(targetPageCounts.values()).reduce((sum, p) => sum + p, 0);
    const reclassifyPattern =
      newPatternId !== PATTERN_ID_OTHER ? await getPattern(newPatternId).catch(() => null) : null;
    const reclassifyGeminiModel = reclassifyPattern?.gemini_model ?? GEMINI_MODEL_DEFAULT;
    try {
      if (newPatternId !== PATTERN_ID_OTHER && company) {
        await executeProvisionalOcrUsage(
          company,
          totalPages,
          baseRemainingCount,
          isProxyMode,
          reclassifyGeminiModel,
          false,
          1,
          [],
          baseRemainingJpy,
        );
        provisionalDeducted = true;
      }
      const statusData =
        newPatternId === PATTERN_ID_OTHER
          ? { pattern_id: newPatternId, ocr_status: ocrStatus.error }
          : { pattern_id: newPatternId, ocr_status: ocrStatus.processing };
      for (const resultId of targetIds) {
        await editResult({ resultId, data: statusData, userId: user?.uid });
      }
      setSelectedIds(new Set());
      setInfoText(`${totalCount}件の仕分けを変更しました`);
      setSnackbarOpen(true);
      setReclassifyLoading(false);
      const creditRate = company
        ? getOcrCreditRateWithModel(company.plan, reclassifyGeminiModel)
        : 0;
      const budgetRemainingCount =
        baseRemainingCount !== null ? Math.max(0, baseRemainingCount) : 0;
      let cumulativePages = 0;
      for (const resultId of targetIds) {
        const resultPageCount = targetPageCounts.get(resultId) || 1;
        const pagesBeforeThis = cumulativePages;
        cumulativePages += resultPageCount;
        const excessPages =
          Math.max(0, cumulativePages - budgetRemainingCount) -
          Math.max(0, pagesBeforeThis - budgetRemainingCount);
        const taskCreditAmount = excessPages * creditRate;
        void handleReOcrPatternResult({
          resultId,
          patternId: newPatternId,
          itemList,
          isReclassification: true,
          provisionalDeducted,
          feProvisionalCreditAmount: taskCreditAmount,
          feProvisionalUsageCount: resultPageCount,
          isProxy: isProxyMode,
          operatorId: isProxyMode ? operatorId || undefined : undefined,
        });
      }
    } catch (error) {
      const message = getUserErrorMessage(error, '仕分け変更に失敗しました');
      setErrorText(message);
      setSnackbarOpen(true);
      setReclassifyLoading(false);
    }
  };

  const handleSingleReclassify = (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): void => {
    if (!singleReclassifyTargetId) return;
    if (!checkCreditBeforeReclassify(newPatternId, false)) return;
    void executeSingleReclassify(newPatternId, itemList);
  };

  const executeSingleReclassify = async (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): Promise<void> => {
    if (!singleReclassifyTargetId) return;
    setReclassifyModalOpen(false);
    setReclassifyLoading(true);
    const targetResult = showData.find(d => d.id === singleReclassifyTargetId);
    const singlePageCount = targetResult?.originalFiles?.length || targetResult?.files?.length || 1;
    let feProvisionalCreditAmount = 0;
    let provisionalDeducted = false;
    try {
      if (newPatternId !== PATTERN_ID_OTHER && company) {
        const singleReclassifyPattern = await getPattern(newPatternId);
        const singleReclassifyGeminiModel =
          singleReclassifyPattern?.gemini_model ?? GEMINI_MODEL_DEFAULT;
        const provisionalResult = await executeProvisionalOcrUsage(
          company,
          singlePageCount,
          baseRemainingCount,
          isProxyMode,
          singleReclassifyGeminiModel,
          false,
          1,
          [],
          baseRemainingJpy,
        );
        feProvisionalCreditAmount = provisionalResult.feProvisionalCreditAmount;
        provisionalDeducted = true;
      }
      const statusData =
        newPatternId === PATTERN_ID_OTHER
          ? { pattern_id: newPatternId, ocr_status: ocrStatus.error }
          : { pattern_id: newPatternId, ocr_status: ocrStatus.processing };
      await editResult({
        resultId: singleReclassifyTargetId,
        data: statusData,
        userId: user?.uid,
      });
      setInfoText('仕分けを変更しました');
      setSnackbarOpen(true);
      setReclassifyLoading(false);
      setSingleReclassifyTargetId('');
      void handleReOcrPatternResult({
        resultId: singleReclassifyTargetId,
        patternId: newPatternId,
        itemList,
        isReclassification: true,
        provisionalDeducted,
        feProvisionalCreditAmount,
        feProvisionalUsageCount: singlePageCount,
        isProxy: isProxyMode,
        operatorId: isProxyMode ? operatorId || undefined : undefined,
      });
    } catch (error) {
      const message = getUserErrorMessage(error, '仕分け変更に失敗しました');
      setErrorText(message);
      setSnackbarOpen(true);
      setReclassifyLoading(false);
      setSingleReclassifyTargetId('');
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.checked) {
      setSelectedIds(new Set(showData.map(d => d.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string): void => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // データ取得関数（resultデータ用）
  const fetchResults = useCallback(
    async (currentLastVisible: DocumentSnapshot | null): Promise<void> => {
      if (!user) return;

      fetchIdRef.current += 1;
      const currentFetchId = fetchIdRef.current;
      setLoading(true);

      try {
        if (unsubscribeRef.current) unsubscribeRef.current();

        unsubscribeRef.current = await getPatternResultsOneTimePaginated(
          FREE_OCR_PATTERN_ID,
          selectedTab,
          RESULT_LIMIT,
          currentLastVisible,
          async (results, newLastVisible, isLastPage) => {
            try {
              if (currentFetchId !== fetchIdRef.current) return;

              const updatedResults: ResultWithUserType[] = results.map(data => {
                const resultData = data as unknown as ResultType;
                return {
                  ...resultData,
                  showName: getDisplayName(resultData.user_name, resultData.is_proxy),
                  iconUrl: resultData.is_proxy ? HiStoryIcon : resultData.user_icon_url || null,
                } as ResultWithUserType;
              });

              setRealtimeData(prev =>
                currentLastVisible === null ? updatedResults : [...prev, ...updatedResults],
              );
              setLastVisible(newLastVisible);
              setHasMore(!isLastPage);
              if (currentLastVisible === null) {
                setShowDataFlg(true);
              } else if (updatedResults.length > 0) {
                setShowDataFlg(true);
              }
            } catch {
              setErrorText('データ処理に失敗しました');
              setSnackbarOpen(true);
            } finally {
              setLoading(false);
            }
          },
          undefined,
          isDetailView,
          effectiveCompanyId || user?.company_id,
        );
      } catch (e) {
        let errorMessage = 'データ取得に失敗しました';
        if (e instanceof Error && e.message.includes('permission-denied')) {
          errorMessage = 'アクセス権限がありません。ログインし直してください。';
        }
        setErrorText(errorMessage);
        setSnackbarOpen(true);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    },
    [
      user,
      selectedTab,
      isDetailView,
      isProxyMode,
      effectiveCompanyId,
      setErrorText,
      setSnackbarOpen,
    ],
  );

  // fetchResultsの最新版をrefで保持（onCompletedコールバックのstale closure防止）
  fetchResultsRef.current = fetchResults;

  const fetchMoreData = (): void => {
    if (hasMore && lastVisible) {
      void fetchResults(lastVisible);
    }
  };

  // データをリフレッシュ（アップロード後の新しいレコード表示用）
  const refreshData = useCallback(async (): Promise<void> => {
    setRealtimeData([]);
    setLastVisible(null);
    setHasMore(false);
    setShowDataFlg(null);
    setSelectedIds(new Set());
    if (fetchResultsRef.current) {
      await fetchResultsRef.current(null);
    }
  }, []);

  // 担当者変更ダイアログを開く（単一・三点リーダーから）#2642
  const openSingleAssigneeDialog = (resultId: string): void => {
    const target = showData.find(d => d.id === resultId);
    setAssigneeDialogMode('single');
    setAssigneeDialogTargets([resultId]);
    setAssigneeDialogCurrent(target?.assignee_id ?? undefined);
    setAssigneeDialogLabel(target?.originalFiles?.[0]?.file_name);
    setAssigneeDialogOpen(true);
  };

  // 担当者変更ダイアログを開く（一括・ヘッダから）#2642
  const openBulkAssigneeDialog = (): void => {
    setAssigneeDialogMode('bulk');
    setAssigneeDialogTargets(Array.from(selectedIds));
    setAssigneeDialogCurrent(undefined);
    setAssigneeDialogLabel(undefined);
    setAssigneeDialogOpen(true);
  };

  // 担当者変更成功時のハンドラ #2642
  const handleAssigneeChangeSuccess = (): void => {
    if (assigneeDialogMode === 'bulk') {
      setSelectedIds(new Set());
    }
    setInfoText(assigneeLabels.changeSuccess);
    setSnackbarOpen(true);
    void refreshData();
  };

  const handleAssigneeChangeError = (message: string): void => {
    setErrorText(message);
    setSnackbarOpen(true);
  };

  // 通常時（検索条件なし）のデータ取得
  // fetchResults を依存に含める（#3203）。含めないと、URL の ?proxy= から代理モードが
  // 後から確立して fetchResults が作り直されても再取得されず、運営企業の一覧が残る。
  useEffect(() => {
    if (isLoading || !user) return;
    if (hasActiveSearch) return;
    void fetchResults(null);
    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, [user, isLoading, selectedTab, hasActiveSearch, fetchResults]);

  // 検索条件が入力されたら Algolia で全件検索する
  useEffect(() => {
    if (!user || !hasActiveSearch) return;
    setIsSearchRefreshing(true);
    setSearchData([]);
    const doSearch = async (): Promise<void> => {
      try {
        const results = await searchPatternResultsByAlgolia({
          patternId: FREE_OCR_PATTERN_ID,
          status: selectedTab,
          freeWord: watchSearchItems.freeWord,
          fileName: watchSearchItems.fileName,
          responsiblePerson: watchSearchItems.responsiblePerson,
          assignee: watchSearchItems.assignee,
          startDate: watchSearchItems.startDate,
          endDate: watchSearchItems.endDate,
          ocrStatus: watchSearchItems.ocrStatus,
          companyId: effectiveCompanyId || user.company_id || undefined,
        });
        const mapped: ResultWithUserType[] = results.map(data => {
          const resultData = data as unknown as ResultType;
          return {
            ...resultData,
            showName: getDisplayName(resultData.user_name, resultData.is_proxy),
            iconUrl: resultData.is_proxy ? HiStoryIcon : resultData.user_icon_url || null,
          } as ResultWithUserType;
        });
        setSearchData(mapped);
        setShowDataFlg(true);
      } catch {
        setErrorText('検索に失敗しました');
        setSnackbarOpen(true);
      } finally {
        setIsSearchRefreshing(false);
      }
    };
    void doSearch();
  }, [
    user,
    hasActiveSearch,
    selectedTab,
    watchSearchItems.freeWord,
    watchSearchItems.fileName,
    watchSearchItems.responsiblePerson,
    watchSearchItems.assignee,
    watchSearchItems.startDate,
    watchSearchItems.endDate,
    watchSearchItems.ocrStatus,
    effectiveCompanyId,
  ]);

  // タブ別件数の取得（リアルタイム監視）
  useEffect(() => {
    if (!user) return;
    const statuses = [
      processingStatus.processing,
      processingStatus.completed,
      processingStatus.outputted,
    ];
    const unsubscribes: Array<() => void> = [];
    const loadCounts = async (): Promise<void> => {
      for (const s of statuses) {
        const unsub = await getPatternResultsCountByStatus(
          FREE_OCR_PATTERN_ID,
          s,
          count => {
            if (s === processingStatus.processing) setProcessingCount(count);
            else if (s === processingStatus.completed) setCompletedCount(count);
            else if (s === processingStatus.outputted) setOutputtedCount(count);
          },
          effectiveCompanyId ?? undefined,
        );
        unsubscribes.push(unsub);
      }
    };
    void loadCounts();
    return () => unsubscribes.forEach(u => u());
  }, [user, effectiveCompanyId]);

  // 担当者候補リストをロード（#2642）
  // 代理モード時は対象企業、通常時はログインユーザー所属会社
  useEffect(() => {
    if (isLoading || !user) return;
    const companyId = effectiveCompanyId || user.company_id;
    if (!companyId) {
      setAssigneeOptions([]);
      return;
    }
    void (async (): Promise<void> => {
      try {
        const users = await getUsersByCompanyId(companyId);
        const list: AssigneeOption[] = users
          .filter(u => u.register_status === registerStatus.finished)
          .map(u => ({
            uid: u.uid as string,
            name: (u.name as string) ?? '',
            iconUrl: (u.icon_url as string | undefined) ?? null,
          }))
          .sort((a, b) => a.name.localeCompare(b.name, 'ja'));
        setAssigneeOptions(list);
      } catch {
        setAssigneeOptions([]);
      }
    })();
  }, [user, isLoading, effectiveCompanyId]);

  // 残りアップロード可能枚数をリアルタイム監視
  useEffect(() => {
    if (!company?.company_id) return;
    if (isProxyMode) {
      setBaseRemainingCount(9999);
      return;
    }
    const companyDocRef = doc(db, 'companies', company.company_id);
    const unsubscribe = onSnapshot(
      companyDocRef,
      snapshot => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const currentMonthOcrCost = data.current_month_ocr_cost ?? 0;
          const ocrMonthlyBudget = data.ocr_monthly_budget ?? company.ocr_monthly_budget;
          const creditRate = getCreditRateByPlan(data.plan ?? company.plan);
          const baseRemaining = Math.floor((ocrMonthlyBudget - currentMonthOcrCost) / creditRate);
          setBaseRemainingCount(Math.max(0, baseRemaining));
          setBaseRemainingJpy(Math.max(0, ocrMonthlyBudget - currentMonthOcrCost));
        }
      },
      error => {
        if (error instanceof FirebaseError && error.code === 'permission-denied') return;
        Sentry.captureException(error);
      },
    );
    return () => unsubscribe();
  }, [company, isProxyMode]);

  // 検索クリア関数
  const handleClearSearch = useCallback((): void => {
    setWatchSearchItems({
      fileName: null,
      responsiblePerson: null,
      assignee: null,
      startDate: null,
      endDate: null,
      freeWord: null,
      ocrStatus: null,
    });
    setSearchData([]);
  }, []);

  // ファイルアップロード処理
  const onSubmit = async (data: { images: File[] }): Promise<void> => {
    setIsSubmitted(true);
    if (!user) {
      setErrorText('ユーザー情報が見つかりません');
      setSnackbarOpen(true);
      return;
    }
    if (!isProxyMode && company && baseRemainingCount !== null) {
      const uploadPageCount = await calculateTotalPageCount(data.images);
      const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
      const overMessage = getOcrLimitOverMessage(uploadPageCount, limitInfo);
      if (overMessage) {
        setErrorText(overMessage);
        setSnackbarOpen(true);
        return;
      }
    }

    const uploadPageCountForLimit = await calculateTotalPageCount(data.images);
    if (uploadPageCountForLimit > MAX_TOTAL_PAGES) {
      setErrorText(
        `合計ページ数が上限を超えています。（${uploadPageCountForLimit}ページ / 上限${MAX_TOTAL_PAGES}ページ）`,
      );
      setSnackbarOpen(true);
      return;
    }

    // 個人別 月間OCR上限チェック（#3133）。代理モードは対象外。
    // 選択時にもインライン表示するが、表示後に他端末で消費された場合に備え
    // 最終防波堤として最新値で再判定する。
    if (!isProxyMode) {
      const companyIdForUsage = effectiveCompanyId ?? user.company_id;
      const execUserDoc = await getUser(user.uid);
      const monthlyLimit = getUserMonthlyOcrLimit((execUserDoc as UserType | undefined) ?? null);
      if (monthlyLimit !== null && companyIdForUsage) {
        const usedPages = await getMonthlyUsageCountByUserId(companyIdForUsage, user.uid);
        const userLimitMessage = getUserMonthlyOcrLimitMessage(
          monthlyLimit,
          usedPages,
          uploadPageCountForLimit,
        );
        if (userLimitMessage) {
          setErrorText(userLimitMessage);
          setSnackbarOpen(true);
          return;
        }
      }
    }

    if (processingTypeValue === fileProcessingType.singleResult) {
      for (const file of data.images) {
        if (isPdfFile(file)) {
          const pageCount = await getPdfPageCount(file);
          if (pageCount > MAX_PAGES_PER_FILE) {
            setErrorText(
              `「${file.name}」のページ数が上限を超えています（${pageCount}ページ / 上限${MAX_PAGES_PER_FILE}ページ）。`,
            );
            setSnackbarOpen(true);
            return;
          }
        }
      }
    }

    setErrorText('');

    let allowAiLearning = true;
    if (user.company_id) {
      try {
        const companyData = await getCompanyForAccount(user.company_id);
        if (companyData) allowAiLearning = companyData.allow_ai_learning;
      } catch {
        // デフォルト値(true)で継続
      }
    }

    const pageFileNames: string[] = [];
    for (const file of data.images) {
      if (isPdfFile(file) && processingTypeValue === fileProcessingType.pagePerResult) {
        const count = await getPdfPageCount(file);
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        const ext = file.name.match(/\.[^/.]+$/)?.[0] ?? '';
        for (let i = 1; i <= count; i++) {
          pageFileNames.push(count > 1 ? `${nameWithoutExt}_${i}${ext}` : file.name);
        }
      } else {
        pageFileNames.push(file.name);
      }
    }

    // 担当者情報を解決（#2642）
    // 未選択（空文字）の場合は null として保存（#2642 レビューFB）
    // 候補リストに見つからなかった場合も作成者の値にフォールバックせず null を保存する（#2872）
    const selectedAssignee = selectedAssigneeUid
      ? assigneeOptions.find(o => o.uid === selectedAssigneeUid)
      : null;
    const resolvedAssigneeId: string | null = selectedAssignee?.uid ?? null;
    const resolvedAssigneeName: string | null = selectedAssignee?.name ?? null;
    const resolvedAssigneeIconUrl: string | null = selectedAssignee?.iconUrl ?? null;

    // 実際に生成される結果数（#3131）
    // pagePerResult: 1ページ=1結果なので合計ページ数 / singleResult: 1ファイル=1結果なのでファイル数
    const resultCount =
      processingTypeValue === fileProcessingType.pagePerResult
        ? uploadPageCountForLimit
        : data.images.length;

    // バックグラウンドでアップロード開始
    startUpload({
      type: 'pattern',
      files: data.images,
      originalFiles: data.images,
      totalPageCount: resultCount,
      pageFileNames,
      userId: user.uid,
      userName: user.name || '',
      userIconUrl: user.icon,
      assigneeId: resolvedAssigneeId,
      assigneeName: resolvedAssigneeName,
      assigneeIconUrl: resolvedAssigneeIconUrl,
      processingType: processingTypeValue,
      geminiModel: selectedGeminiModel,
      companyId: effectiveCompanyId ?? user.company_id ?? undefined,
      allowAiLearning,
      isProxyMode,
      operatorId: isProxyMode ? operatorId || undefined : undefined,
      operatorRole: isProxyMode ? operatorRole || undefined : undefined,
      company,
      baseRemainingCount,
      baseRemainingJpy,
      patternId: FREE_OCR_PATTERN_ID,
      patternName: 'フリーOCR',
      itemList: [],
      thinkingBudget: thinkingBudgetValue ?? geminiThinkingBudget.disabled,
      thinkingLevel: isOperatorStaff(user?.role) ? thinkingLevelValue : null,
      onCompleted: refreshData,
    });

    setUploadModalOpen(false);
    reset({ images: [] });
    setInfoText(
      `${resultCount}件のフリーOCRタスクが開始されました。完了までしばらくお待ちください。`,
    );
    setSnackbarOpen(true);
  };

  // アップロードファイル選択時
  const handleFilesSelected = async (
    files: FileList | null,
    onChange: (value: File[]) => void,
  ): Promise<void> => {
    if (files) {
      const fileArray = Array.from(files);
      const currentImages = watch('images') || [];
      const updatedFiles = [...currentImages, ...fileArray];

      if (processingTypeValue === fileProcessingType.pagePerResult) {
        const newCache = new Map(pdfPageCountCache);
        for (const file of fileArray) {
          if (isPdfFile(file) && !newCache.has(file.name)) {
            const count = await getPdfPageCount(file);
            newCache.set(file.name, count);
          }
        }
        setPdfPageCountCache(newCache);
      }

      onChange(updatedFiles);

      setTimeout(async () => {
        const isValid = await trigger('images');
        const totalPages = await calculateTotalPageCount(updatedFiles);
        if (isValid && totalPages > MAX_TOTAL_PAGES) {
          setError('images', {
            type: 'manual',
            message: `合計ページ数が上限を超えています。（${totalPages}ページ / 上限${MAX_TOTAL_PAGES}ページ）`,
          });
          return;
        }
        if (isValid && processingTypeValue === fileProcessingType.singleResult) {
          for (const file of fileArray) {
            if (isPdfFile(file)) {
              const pageCount = await getPdfPageCount(file);
              if (pageCount > MAX_PAGES_PER_FILE) {
                setError('images', {
                  type: 'manual',
                  message: `「${file.name}」のページ数が上限を超えています（${pageCount}ページ / 上限${MAX_PAGES_PER_FILE}ページ）。`,
                });
                return;
              }
            }
          }
        }
        // 個人別 月間OCR上限（#3133）。会社枠より先に判定する。
        const userLimitMessage = getUserLimitMessage(totalPages);
        if (isValid && userLimitMessage) {
          setError('images', { type: 'manual', message: userLimitMessage });
          return;
        }
        if (user && baseRemainingCount !== null && company && !isProxyMode) {
          const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
          const overMessage = getOcrLimitOverMessage(totalPages, limitInfo);
          if (isValid && overMessage) {
            setError('images', { type: 'manual', message: overMessage });
          }
        }
      }, 0);
    }
  };

  const handleFilesDeleted = (index: number): void => {
    const currentImages = watch('images');
    const updatedImages = currentImages.filter((_, i) => i !== index);
    reset({ images: updatedImages });
    void trigger('images');
  };

  // 出力方式変更時のバリデーション再実行
  const handleProcessingTypeChange = async (
    newValue: number,
    currentFiles: File[],
  ): Promise<void> => {
    setProcessingTypeValue(newValue);
    if (newValue === fileProcessingType.pagePerResult) {
      const newCache = new Map(pdfPageCountCache);
      for (const file of currentFiles) {
        if (isPdfFile(file) && !newCache.has(file.name)) {
          const count = await getPdfPageCount(file);
          newCache.set(file.name, count);
        }
      }
      setPdfPageCountCache(newCache);
    }
    if (currentFiles.length > 0) {
      if (newValue === fileProcessingType.singleResult) {
        for (const file of currentFiles) {
          if (isPdfFile(file)) {
            const pageCount = await getPdfPageCount(file);
            if (pageCount > MAX_PAGES_PER_FILE) {
              setError('images', {
                type: 'manual',
                message: `「${file.name}」のページ数が上限を超えています（${pageCount}ページ / 上限${MAX_PAGES_PER_FILE}ページ）。`,
              });
              return;
            }
          }
        }
      }
      await trigger('images');
    }
  };

  const handleGeminiModelChange = (newModel: number): void => {
    setSelectedGeminiModel(newModel);
    setThinkingBudgetValue(
      newModel === geminiModel.pro_2_5
        ? geminiThinkingBudget.minimal
        : geminiThinkingBudget.disabled,
    );
    setThinkingLevelValue(null);
  };

  const handleDeleteResult = async (): Promise<void> => {
    try {
      await deleteResult(deleteTargetId);
      setDeleteTargetId('');
      setDeleteModalOpen(false);
      await refreshData();
      setInfoText('ファイルを削除しました。');
      setSnackbarOpen(true);
    } catch {
      setInfoText('');
      setErrorText('削除に失敗しました');
      setSnackbarOpen(true);
    }
  };

  const handleBulkDelete = async (): Promise<void> => {
    try {
      setBulkDeleteLoading(true);
      await Promise.all(Array.from(selectedIds).map(id => deleteResult(id)));
      setSelectedIds(new Set());
      setBulkDeleteModalOpen(false);
      await refreshData();
      setInfoText(`${selectedIds.size}件のファイルを削除しました。`);
      setSnackbarOpen(true);
    } catch {
      setInfoText('');
      setErrorText('削除に失敗しました');
      setSnackbarOpen(true);
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const handleFileExport = async (
    fileType: (typeof exportFileType)[keyof typeof exportFileType],
    encoding: (typeof exportEncoding)[keyof typeof exportEncoding],
    _selectedFields: string[],
    _sortedPattern?: unknown,
    moveToOutputted?: boolean,
    _saveSetting?: boolean,
    _selectedFixedFields?: string[],
    _sortedUnifiedOrder?: string[],
    customFileName?: string,
  ): Promise<void> => {
    if (!user) {
      setErrorText('ユーザー情報が見つかりません');
      setSnackbarOpen(true);
      return;
    }

    try {
      const ext = fileType === exportFileType.excel ? '.xlsx' : '.csv';
      const trimmedCustomName = customFileName?.trim();
      const tempFileName = trimmedCustomName
        ? `${trimmedCustomName}${ext}`
        : generateFreeAiOcrFileName(selectedTab, fileType, encoding);

      const historyId = await createFileExportHistory({
        pattern_id: FREE_OCR_PATTERN_ID,
        pattern_name: 'フリーOCR',
        company_id: effectiveCompanyId ?? user.company_id ?? '',
        file_name: tempFileName,
        file_type: fileType === exportFileType.excel ? 'xlsx' : 'csv',
        created_by: user.uid,
        created_by_name: isProxyMode ? PROXY_DISPLAY_NAME : user.name,
        created_by_icon_url: isProxyMode ? '' : user.icon || '',
        is_proxy: isProxyMode || false,
      });

      setFileExportModalOpen(false);

      // 履歴作成成功後にファイル出力履歴画面へ遷移（出力中ステータスが即座に表示され、
      // 完了時に自動ダウンロードされる）。パターンOCRと挙動を統一する。
      navigate('/ai-ocr/free/exports');

      // 遷移後に参照しないよう、対象IDは遷移直前に退避してからクリアする
      const exportTargetId = fileExportTargetId;
      setFileExportTargetId(null);

      // 重い処理（データ取得・整形・ファイル生成・アップロード）はバックグラウンドで継続
      (async (): Promise<void> => {
        try {
          let results: DocumentData[];
          if (exportTargetId) {
            const targetResult = realtimeData.find(r => r.id === exportTargetId);
            results = targetResult ? [targetResult] : [];
          } else {
            results = await getPatternResultsForExport(
              FREE_OCR_PATTERN_ID,
              selectedTab,
              undefined,
              effectiveCompanyId || user?.company_id,
            );
          }

          // フリーOCRは1件ずつ出力する設計のため、対象1件を縦展開する。
          // 結果詳細画面（FreeAiOcrResult）と同じ生成ロジック・エンコード処理に揃える。
          const verticalRows = generateFreeAiOcrVerticalRows(results[0] ?? {});

          let fileBlob: Blob;

          if (fileType === exportFileType.excel) {
            const XLSX = await import('xlsx');
            const ws = XLSX.utils.aoa_to_sheet(verticalRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'データ');
            const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
            fileBlob = new Blob([buf], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
          } else {
            const csvString = Papa.unparse(verticalRows, { header: false });
            if (encoding === exportEncoding.shiftJis) {
              const sjisArray = Encoding.convert(
                Encoding.stringToCode(csvString),
                'SJIS',
                'UNICODE',
              );
              fileBlob = new Blob([new Uint8Array(sjisArray)], {
                type: 'text/csv;charset=shift_jis',
              });
            } else {
              // UTF-8 は BOM を付与して Excel での文字化けを防ぐ
              const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
              fileBlob = new Blob([bom, new TextEncoder().encode(csvString)], {
                type: 'text/csv;charset=utf-8',
              });
            }
          }

          const reader = new FileReader();
          reader.readAsDataURL(fileBlob);
          reader.onload = async (): Promise<void> => {
            const base64 = (reader.result as string).split(',')[1];
            await exportFile({
              history_id: historyId,
              file_content: base64,
              file_size: fileBlob.size,
              encoding: encoding === exportEncoding.shiftJis ? 'sjis' : 'utf8',
            });

            // 出力済ステータスへの移行（パターンOCRと挙動を統一する）
            if (moveToOutputted) {
              try {
                const updatePromises = results
                  .filter(r => r.id)
                  .map(r => {
                    const resultRef = doc(db, 'results', r.id);
                    return updateDoc(resultRef, {
                      processing_status: processingStatus.outputted,
                      updated_at: Timestamp.now(),
                      updated_by: user.uid,
                    });
                  });
                await Promise.all(updatePromises);
              } catch {
                // ステータス更新失敗はファイル出力自体を失敗にしない
              }
            }
          };
        } catch (exportError) {
          Sentry.captureException(exportError);
        }
      })();
    } catch {
      setErrorText('ファイル出力に失敗しました');
      setSnackbarOpen(true);
    }
  };

  const handleTabChange = (newValue: number): void => {
    if (newValue === selectedTab) return;
    setSelectedTab(newValue);
    setRealtimeData([]);
    setSearchData([]);
    setLastVisible(null);
    setHasMore(false);
    setShowDataFlg(null);
    setSelectedIds(new Set());
  };

  const styles = {
    avatar: {
      height: '24px',
      width: '24px',
    },
    buttonLarge: {
      height: '44px',
      padding: '16px',
      '& .MuiButton-icon': {
        marginRight: '4px',
      },
      '@media screen and (max-width:960px)': {
        width: 'calc(50% - 8px)',
      },
      '@media screen and (max-width:600px)': {
        width: '100%',
      },
    },
    headerButtonWrapper: {
      display: 'flex',
      gap: '16px',
      '@media screen and (max-width:960px)': {
        boxSizing: 'border-box',
        position: 'fixed',
        bottom: 0,
        left: 0,
        backgroundColor: white_overlay80,
        padding: '16px 16px 24px',
        width: '100%',
        zIndex: 1,
      },
      '@media screen and (max-width:600px)': {
        flexDirection: 'column',
        gap: '12px',
      },
    },
    headerTitle: {
      ...boldXxl,
      color: gray100,
      '@media screen and (max-width:960px)': {
        ...spHeaderTitle,
      },
    },
    icon24: {
      height: '24px',
      marginLeft: '0px',
      width: '24px',
    },
    tab: {
      alignItems: 'center',
      backgroundColor: gray40,
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      height: '24px',
      justifyContent: 'center',
      width: '120px',
      '@media screen and (max-width:960px)': {
        width: 'calc(100% / 3)',
        height: 'auto',
        minHeight: '24px',
        padding: '2px 4px',
        boxSizing: 'border-box',
      },
    },
    tabActive: {
      backgroundColor: gray100,
    },
    tabText: {
      ...regularXs,
      color: gray100,
      '@media screen and (max-width:960px)': {
        fontSize: '11px',
        textAlign: 'center',
      },
    },
    tabTextActive: {
      color: white,
    },
    tabAndButtonWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      margin: '0 32px',
      '@media screen and (max-width:960px)': {
        flexWrap: 'wrap',
        margin: '0 16px',
        width: 'calc(100% - 32px)',
      },
    },
    tabWrapper: {
      display: 'flex',
      gap: '12px',
      '@media screen and (max-width:960px)': {
        width: '100%',
      },
    },
    table: {
      minWidth: '960px',
      overflowX: 'auto',
    },
    tableCellButton: {
      padding: 0,
    },
    tableDataText: {
      ...regularSm,
      color: gray100,
      // 行の高さをホーム画面の処理履歴／パターンOCR一覧に合わせて上下 padding を確保する
      padding: '12px 16px',
    },
    tableHeadText: {
      ...regularSm,
      color: gray80,
      padding: '0 16px',
    },
    tableHeadRow: {
      height: '48px',
    },
    tableDataRow: {
      height: '48px',
      '&:hover': {
        backgroundColor: gray20,
        cursor: 'pointer',
      },
    },
    tableSection: {
      backgroundColor: white,
      border: '1px solid' + gray40,
      borderRadius: '4px',
      boxSizing: 'border-box',
      margin: '0 32px',
      minHeight: '560px',
      padding: '16px 24px 56px',
      width: 'calc(100% - 64px)',
      '@media screen and (max-width:960px)': {
        border: 'none',
        borderRadius: 0,
        margin: 0,
        width: '100%',
      },
    },
    tableWrapper: {
      overflowX: 'auto',
      width: '100%',
      WebkitOverflowScrolling: 'touch',
    },
    titleAndStatusWrapper: {
      flexGrow: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginRight: '18px',
      '@media screen and (max-width: 960px)': {
        marginRight: '0',
        fontSize: '12px',
      },
    },
    mainEllipsisText: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '450px',
      display: 'inline-block',
      verticalAlign: 'middle',
    },
    icon: {
      width: '56px',
      height: '56px',
      margin: '80px 0 24px 0',
      '@media screen and (max-width: 600px)': {
        margin: '24px 0 24px 0',
      },
    },
    title: {
      whiteSpace: 'pre-wrap',
      mb: 2,
      fontWeight: '500',
      color: gray100,
      fontSize: '22px',
      fontStyle: 'normal',
      lineHeight: '1.4',
    },
  };

  // タブコンポーネント
  const TabItem = ({ status }: { status: number }): ReactElement => {
    const statusLabels = {
      [processingStatus.processing]: processingStatusLabel[processingStatus.processing],
      [processingStatus.completed]: processingStatusLabel[processingStatus.completed],
      [processingStatus.outputted]: processingStatusLabel[processingStatus.outputted],
    };

    const getCount = (): number | null => {
      if (status === processingStatus.processing) return processingCount;
      if (status === processingStatus.completed) return completedCount;
      if (status === processingStatus.outputted) return outputtedCount;
      return null;
    };

    const count = getCount();
    const label = count !== null ? `${statusLabels[status]}（${count}件）` : statusLabels[status];

    return (
      <Box
        sx={[styles.tab, selectedTab === status && styles.tabActive]}
        onClick={(): void => handleTabChange(status)}
      >
        <Typography sx={[styles.tabText, selectedTab === status && styles.tabTextActive]}>
          {label}
        </Typography>
      </Box>
    );
  };

  const titleLabel = ((): string => {
    switch (selectedTab) {
      case processingStatus.completed:
        return 'フリーOCRの確認済ファイルがありません';
      case processingStatus.outputted:
        return 'フリーOCRの出力済ファイルがありません';
      default:
        return 'フリーOCRの読取中ファイルがありません';
    }
  })();

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle customTitle="フリーOCR" />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>
      <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
        <Box sx={layoutStyles.header}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              minWidth: 0,
              marginRight: '18px',
              '@media screen and (max-width: 960px)': { marginRight: '0' },
            }}
          >
            <Box sx={styles.titleAndStatusWrapper}>
              <Typography sx={styles.headerTitle}>フリーOCR</Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '13px',
                color: 'text.secondary',
                textAlign: 'left',
                '@media screen and (max-width:960px)': { display: 'none' },
              }}
            >
              パターン設定不要で、アップロードするだけでAIが帳票のデータを自動抽出します。
            </Typography>
          </Box>
          <Box sx={styles.headerButtonWrapper}>
            <Button
              onClick={(): void => {
                void (async (): Promise<void> => {
                  setIsSubmitted(false);
                  // 月間OCR枠を消費し切っている場合は警告ダイアログを表示して中断（#2888）
                  // 代理モードはカウント・クレジット対象外のためスキップ
                  if (!isProxyMode && company && baseRemainingCount !== null) {
                    const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
                    if (limitInfo.totalRemainingCount < 1) {
                      setLimitDialogOpen(true);
                      return;
                    }
                  }
                  // 担当者デフォルト値（#2642）。通常=本人 / 代理=企業担当者（取得不可なら未設定）
                  const defaultAssignee = await resolveDefaultAssignee({
                    isProxyMode,
                    companyId: effectiveCompanyId,
                    user: { uid: user?.uid ?? '' },
                  });
                  setSelectedAssigneeUid(defaultAssignee.uid ?? '');
                  setUploadModalOpen(true);
                })();
              }}
              startIcon={<CloudUploadOutlined sx={styles.icon24} />}
              sx={[primaryButtonBackgroundColor, styles.buttonLarge]}
            >
              <Typography sx={buttonTextIconLarge}>新規文字読取</Typography>
            </Button>
          </Box>
        </Box>

        <Grid2 container flexDirection={'column'} gap={'16px'} width={'100%'}>
          <Box
            sx={{
              marginLeft: '32px',
              marginRight: '32px',
              width: 'calc(100% - 64px)',
              '@media screen and (max-width:960px)': {
                marginLeft: '16px',
                marginRight: '16px',
                width: 'calc(100% - 32px)',
              },
            }}
          >
            <AiOcrSearchForm
              watchSearchItems={watchSearchItems}
              setWatchSearchItems={setWatchSearchItems}
              onClearSearch={handleClearSearch}
              hideCreatedBy
              // フリーOCRは要注意の判定を行っていない（needs_review を持たない）ため出さない（#3334）
              hideNeedsReview
            />
          </Box>
          <Box sx={styles.tabAndButtonWrapper}>
            <Box sx={styles.tabWrapper}>
              <TabItem status={processingStatus.processing} />
              <TabItem status={processingStatus.completed} />
              <TabItem status={processingStatus.outputted} />
            </Box>
            {/* 一括操作ボタン群（チェック選択時のみ表示）#2642
                狭い画面でも見切れないようアイコン+件数のみ表示し、機能名はTooltip。
                担当者変更 → 仕分け変更 → 削除 の順（PatternAiOcr とUI統一） */}
            {selectedIds.size > 0 && (
              <Box sx={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
                <Tooltip title={assigneeLabels.changeAction}>
                  <Button
                    onClick={openBulkAssigneeDialog}
                    sx={[
                      secondaryButtonBackgroundColor,
                      {
                        height: '30px',
                        minWidth: 'auto',
                        padding: '4px 10px',
                        gap: '4px',
                      },
                    ]}
                  >
                    <PersonOutline sx={{ fontSize: '18px' }} />
                    <Typography sx={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                      ({selectedIds.size})
                    </Typography>
                  </Button>
                </Tooltip>
                <Tooltip title={reclassifyLoading ? '仕分け変更中...' : '仕分け変更'}>
                  <span>
                    <Button
                      onClick={(): void => setReclassifyModalOpen(true)}
                      disabled={reclassifyLoading}
                      sx={[
                        secondaryButtonBackgroundColor,
                        {
                          height: '30px',
                          minWidth: 'auto',
                          padding: '4px 10px',
                          gap: '4px',
                        },
                      ]}
                    >
                      <SwapHoriz sx={{ fontSize: '18px' }} />
                      <Typography sx={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                        ({selectedIds.size})
                      </Typography>
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="削除">
                  <Button
                    onClick={(): void => setBulkDeleteModalOpen(true)}
                    sx={[
                      tertiaryButtonBackgroundColor,
                      {
                        height: '30px',
                        minWidth: 'auto',
                        padding: '4px 10px',
                        gap: '4px',
                      },
                    ]}
                  >
                    <DeleteOutline sx={{ fontSize: '18px' }} />
                    <Typography sx={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                      ({selectedIds.size})
                    </Typography>
                  </Button>
                </Tooltip>
              </Box>
            )}
            <Box sx={{ marginLeft: 'auto', display: 'flex', gap: '12px' }}>
              <Button
                onClick={(): void => navigate('/ai-ocr/free/exports')}
                sx={{
                  height: '30px',
                  whiteSpace: 'nowrap',
                  minWidth: 'auto',
                  backgroundColor: white,
                  border: `1px solid ${gray80}`,
                  color: gray80,
                  '&:hover': {
                    backgroundColor: white,
                    border: `1px solid ${gray60}`,
                    color: gray60,
                    opacity: '80%',
                  },
                }}
              >
                <Typography sx={{ fontSize: '14px', whiteSpace: 'nowrap' }}>
                  ファイル出力履歴
                </Typography>
              </Button>
            </Box>
          </Box>

          <Box sx={styles.tableSection}>
            {/* 未認証時は useAuthRedirect がログイン画面へ遷移させるまでの間だけローディングを出す。
                showDataFlg は fetchResults の中でしか確定しないため、この条件がないと
                未認証のまま画面が固まる（#3219） */}
            {isLoading || !user || showDataFlg === null ? (
              <CustomLoading loading={true} />
            ) : isSearchRefreshing ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <CircularProgress />
              </Box>
            ) : showDataFlg && showData.length > 0 ? (
              <Box sx={styles.tableWrapper}>
                <InfiniteScroll
                  dataLength={showData.length}
                  next={fetchMoreData}
                  hasMore={hasMore}
                  loader={<CustomLoading loading={loading} />}
                >
                  <Table sx={styles.table}>
                    <TableHead>
                      <TableRow sx={styles.tableHeadRow}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isIndeterminate}
                            onChange={handleSelectAll}
                            sx={{
                              color: gray80,
                              '&.Mui-checked': { color: gray80 },
                              '&.MuiCheckbox-indeterminate': { color: gray80 },
                            }}
                          />
                        </TableCell>
                        {/* ファイル名: ホーム画面／パターンOCRに合わせ、ファイル名(1行目)＋ステータス(2行目)を2行で表示 */}
                        <TableCell
                          sx={{
                            ...styles.tableHeadText,
                            boxSizing: 'border-box',
                            minWidth: '280px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          ファイル名
                        </TableCell>
                        {/* 余白吸収用スペーサー列: 実データ列を左詰めにし、担当者・アップロード日時・三点リーダーを右端固定する */}
                        <TableCell aria-hidden sx={{ width: '100%', padding: 0 }} />
                        {/* 担当者は右端側に固定する（パターンOCRと統一） */}
                        <TableCell
                          sx={{
                            ...styles.tableHeadText,
                            boxSizing: 'border-box',
                            width: '160px',
                            minWidth: '160px',
                            maxWidth: '160px',
                          }}
                        >
                          {assigneeLabels.label}
                        </TableCell>
                        {/* アップロード日時: 担当者の右に配置（パターンOCRと統一） */}
                        <TableCell
                          sx={{
                            ...styles.tableHeadText,
                            boxSizing: 'border-box',
                            width: '170px',
                            minWidth: '170px',
                            maxWidth: '170px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          アップロード日時
                        </TableCell>
                        <TableCell
                          sx={{
                            boxSizing: 'border-box',
                            width: '48px',
                            minWidth: '48px',
                            maxWidth: '48px',
                          }}
                        />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {showData.map((data, index) => {
                        const isDisabled =
                          !isOperatorStaff(user?.role) &&
                          (data.ocr_status === ocrStatus.processing ||
                            data.ocr_status === ocrStatus.error);
                        const isDeletedResult = data.status === resultStatus.deleted;

                        return (
                          <TableRow
                            key={index}
                            onClick={
                              isDisabled
                                ? undefined
                                : (): void => {
                                    const navigationResultIds = showData.map(d => d.id);
                                    navigate(`/ai-ocr/free/result/${data.id}`, {
                                      state: { navigationResultIds },
                                    });
                                  }
                            }
                            sx={{
                              ...styles.tableDataRow,
                              cursor: isDisabled ? 'default' : 'pointer',
                              '&:hover': isDisabled ? {} : styles.tableDataRow['&:hover'],
                              ...(isDeletedResult && { opacity: 0.6, backgroundColor: '#f5f5f5' }),
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedIds.has(data.id)}
                                onChange={(e): void => {
                                  e.stopPropagation();
                                  handleSelectOne(data.id);
                                }}
                                onClick={(e): void => e.stopPropagation()}
                                sx={{
                                  color: gray80,
                                  '&.Mui-checked': { color: gray80 },
                                }}
                              />
                            </TableCell>
                            {/* ファイル名／読取ステータス（ホーム画面・パターンOCRに合わせ、アイコン＋ファイル名＋ステータスの2行表示） */}
                            <TableCell sx={{ ...styles.tableDataText, minWidth: '280px' }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  minWidth: 0,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '32px',
                                    height: '40px',
                                    flexShrink: 0,
                                  }}
                                >
                                  <OcrStatusIcon status={data.ocr_status} />
                                </Box>
                                <OcrFileNameStatus
                                  fileName={data.originalFiles?.[0]?.file_name || '-'}
                                  statusText={((): string => {
                                    switch (data.ocr_status) {
                                      case ocrStatus.error:
                                        return '読取エラー';
                                      case ocrStatus.completed:
                                        return '読取完了';
                                      case ocrStatus.uploaded:
                                      case ocrStatus.processing:
                                        return '読取中';
                                      default:
                                        return '';
                                    }
                                  })()}
                                  statusTooltip={
                                    data.ocr_status === ocrStatus.error ? (
                                      <>
                                        ファイルの読み取りに失敗しました。
                                        <br />
                                        再度アップロードをお願いします。
                                      </>
                                    ) : undefined
                                  }
                                  fileNameSuffix={isDeletedResult ? <DeletedBadge /> : undefined}
                                />
                              </Box>
                            </TableCell>
                            {/* 余白吸収用スペーサー列（ヘッダーと列数を揃える。担当者・アップロード日時・三点リーダーを右端固定） */}
                            <TableCell aria-hidden sx={{ width: '100%', padding: 0 }} />
                            {/* 担当者（#2642）退会済ユーザーは「退会済ユーザー」表記。右端側に固定（パターンOCRと統一） */}
                            <TableCell
                              sx={{
                                boxSizing: 'border-box',
                                padding: '0 16px',
                                width: '160px',
                                minWidth: '160px',
                                maxWidth: '160px',
                              }}
                            >
                              {((): ReactElement => {
                                const { displayName, displayIconUrl, isWithdrawn } =
                                  resolveAssigneeDisplay(
                                    data.assignee_id,
                                    data.assignee_name,
                                    data.assignee_icon_url,
                                    assigneeOptions,
                                  );
                                if (isWithdrawn) {
                                  return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <Typography sx={{ color: gray60 }}>
                                        <span
                                          style={
                                            {
                                              ...styles.mainEllipsisText,
                                              maxWidth: '128px',
                                            } as React.CSSProperties
                                          }
                                        >
                                          {displayName}
                                        </span>
                                      </Typography>
                                    </Box>
                                  );
                                }
                                return (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Avatar
                                      src={displayIconUrl || NoAccountIcon}
                                      sx={{ ...styles.avatar, flexShrink: 0 }}
                                    />
                                    <Typography sx={{ lineHeight: '24px' }}>
                                      <Tooltip title={displayName}>
                                        <span
                                          style={
                                            {
                                              ...styles.mainEllipsisText,
                                              maxWidth: '96px',
                                            } as React.CSSProperties
                                          }
                                        >
                                          {displayName}
                                        </span>
                                      </Tooltip>
                                    </Typography>
                                  </Box>
                                );
                              })()}
                            </TableCell>
                            {/* アップロード日時: 担当者の右に配置（パターンOCRと統一） */}
                            <TableCell
                              sx={{
                                ...styles.tableDataText,
                                boxSizing: 'border-box',
                                width: '170px',
                                minWidth: '170px',
                                maxWidth: '170px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatTimestamp(data.created_at)}
                            </TableCell>
                            <TableCell
                              sx={{
                                ...styles.tableCellButton,
                                boxSizing: 'border-box',
                                width: '48px',
                                minWidth: '48px',
                                maxWidth: '48px',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={(event): void => {
                                  event.stopPropagation();
                                  setMenuAnchorEl(event.currentTarget);
                                  setMenuTargetId(data.id);
                                }}
                              >
                                <MoreVert />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </InfiniteScroll>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <DescriptionIcon sx={styles.icon} />
                <Typography sx={styles.title}>{titleLabel}</Typography>
              </Box>
            )}
          </Box>
        </Grid2>
      </Box>

      {/* 三点リーダーメニュー */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={(): void => {
          setMenuAnchorEl(null);
          setMenuTargetId('');
        }}
      >
        {/* 担当者変更 → 仕分け変更 → ファイル出力 → 削除 の順（#2642） */}
        <MenuItem
          onClick={(): void => {
            const targetId = menuTargetId;
            setMenuAnchorEl(null);
            setMenuTargetId('');
            openSingleAssigneeDialog(targetId);
          }}
        >
          <ListItemIcon>
            <PersonOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>{assigneeLabels.changeAction}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(): void => {
            setMenuAnchorEl(null);
            setSingleReclassifyTargetId(menuTargetId);
            setMenuTargetId('');
            setReclassifyModalOpen(true);
          }}
          disabled={reclassifyLoading}
        >
          <ListItemIcon>
            <SwapHoriz fontSize="small" />
          </ListItemIcon>
          <ListItemText>仕分け変更</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(): void => {
            setMenuAnchorEl(null);
            setFileExportTargetId(menuTargetId);
            setFileExportModalOpen(true);
          }}
        >
          <ListItemIcon>
            <FileDownloadOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>ファイル出力</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={(): void => {
            setMenuAnchorEl(null);
            setDeleteTargetId(menuTargetId);
            setMenuTargetId('');
            setDeleteModalOpen(true);
          }}
        >
          <ListItemIcon>
            <DeleteOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>削除</ListItemText>
        </MenuItem>
      </Menu>

      {/* 担当者変更ダイアログ（#2642）single/bulk 共通。親が保持する候補リストを渡して二重フェッチを防ぐ */}
      <AssigneeChangeDialog
        open={assigneeDialogOpen}
        onClose={(): void => setAssigneeDialogOpen(false)}
        mode={assigneeDialogMode}
        targetResultIds={assigneeDialogTargets}
        currentAssigneeId={assigneeDialogCurrent}
        targetLabel={assigneeDialogLabel}
        companyId={effectiveCompanyId ?? user?.company_id ?? ''}
        options={assigneeOptions}
        onSuccess={handleAssigneeChangeSuccess}
        onError={handleAssigneeChangeError}
      />

      {/* アップロードモーダル */}
      <UploadModal
        open={uploadModalOpen}
        onClose={(): void => {
          setUploadModalOpen(false);
          setIsSubmitted(false);
          reset({ images: [] });
        }}
        onSubmit={onSubmit}
        control={control}
        handleSubmit={handleSubmit}
        errors={errors}
        watchedImages={watchedImages}
        isSubmitted={isSubmitted}
        processingTypeValue={processingTypeValue}
        onProcessingTypeChange={handleProcessingTypeChange}
        onFilesSelected={handleFilesSelected}
        onFileDeleted={handleFilesDeleted}
        pdfPageCountCache={pdfPageCountCache}
        selectedGeminiModel={selectedGeminiModel}
        onGeminiModelChange={handleGeminiModelChange}
        thinkingBudgetValue={thinkingBudgetValue}
        onThinkingBudgetChange={(val): void => setThinkingBudgetValue(Number(val))}
        thinkingLevelValue={thinkingLevelValue}
        onThinkingLevelChange={setThinkingLevelValue}
        canUseVerificationSettings={isDetailView}
        isProAllowedPattern={isProAllowedPattern}
        totalPageCount={totalPageCount}
        isCalculating={isCalculating}
        baseRemainingCount={baseRemainingCount}
        user={user}
        company={company}
        fileCountOverText={fileCountOverText}
        proxyDialogSx={proxyDialogSx}
        getUserLimitMessage={getUserLimitMessage}
        showAssigneeSelect
        assigneeOptions={assigneeOptions}
        selectedAssigneeUid={selectedAssigneeUid}
        onAssigneeChange={setSelectedAssigneeUid}
      />

      {/* 個別削除モーダル */}
      <DeleteModal
        open={deleteModalOpen}
        onClose={(): void => setDeleteModalOpen(false)}
        onDelete={handleDeleteResult}
        title="ファイル"
      />

      {/* 一括削除モーダル */}
      <DeleteModal
        open={bulkDeleteModalOpen}
        onClose={(): void => setBulkDeleteModalOpen(false)}
        onDelete={handleBulkDelete}
        title={`${selectedIds.size}件のファイル`}
        loading={bulkDeleteLoading}
      />

      {/* 利用枠上限ダイアログ */}
      <OcrLimitDialog open={limitDialogOpen} onClose={(): void => setLimitDialogOpen(false)} />

      {/* 仕分け変更モーダル */}
      <PatternSelectModal
        open={reclassifyModalOpen}
        onClose={(): void => {
          setReclassifyModalOpen(false);
          setSingleReclassifyTargetId('');
        }}
        onSelect={singleReclassifyTargetId ? handleSingleReclassify : handleBulkReclassify}
        currentPatternId={FREE_OCR_PATTERN_ID}
        companyId={effectiveCompanyId || ''}
        usesCredit={calculateOcrLimitInfo(company, baseRemainingCount).remainingCount <= 0}
        hideWarning={isProxyMode}
      />

      {/* ファイル出力モーダル */}
      <FileExportDialog
        open={fileExportModalOpen}
        onClose={(): void => {
          setFileExportModalOpen(false);
          setFileExportTargetId(null);
        }}
        onExport={handleFileExport}
        pattern={null}
        selectedTab={selectedTab}
        defaultFileName={((): string | undefined => {
          if (!fileExportTargetId) return undefined;
          const target = realtimeData.find(r => r.id === fileExportTargetId);
          const srcName = target?.originalFiles?.[0]?.file_name ?? target?.files?.[0]?.file_name;
          if (!srcName) return undefined;
          const base = srcName.replace(/\.[^/.]+$/, '');
          const ts = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 14);
          return `${base}_${ts}`;
        })()}
      />

      <CustomSnackbar
        open={snackbarOpen}
        onClose={(): void => setSnackbarOpen(false)}
        text={errorText}
        infoText={infoText}
      />
    </Box>
  );
};

export default FreeAiOcr;
